// QuickLinksMenu — "⋯" trigger that reveals a dropdown of company-configured
// fast-access links. Lives in the sidebar header. Hover opens it on desktop;
// click toggles it for touch / keyboard. Click outside closes it.
//
// Behaviour:
//   - Fetches links from the API on first open (cached in state).
//   - Admin users always see a "Manage Quick Links" action at the bottom.
//   - Non-admin users with zero links see nothing (component returns null).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, MoreHorizontal, Settings } from 'lucide-react';
import { fetchQuickLinks, type QuickLinkDto } from '../../api/quickLinks';
import { useAuthStore } from '../../store/authStore';
import styles from './QuickLinksMenu.module.css';

export function QuickLinksMenu() {
  const role = useAuthStore((s) => s.user?.role ?? null);
  // Admin-only gate — Manage Quick Links settings page is admin-only per spec.
  const isAdmin = role === 'admin';

  const [isOpen, setIsOpen] = useState(false);
  const [links, setLinks] = useState<QuickLinkDto[] | null>(null); // null = not yet fetched
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch on first open
  useEffect(() => {
    if (!isOpen || links !== null) return;
    setError(false);
    setLoading(true);
    fetchQuickLinks()
      .then((data) => setLinks(data))
      .catch(() => { setLinks([]); setError(true); })
      .finally(() => setLoading(false));
  }, [isOpen, links]);

  // Click-outside closes the dropdown
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const node = containerRef.current;
      if (node && !node.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  function close() {
    setIsOpen(false);
  }

  // Once we have fetched data: non-admin with zero links → render nothing
  const fetched = links !== null;
  if (fetched && links.length === 0 && !isAdmin) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className={styles.button}
        aria-label="Quick links"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      >
        <MoreHorizontal size={18} aria-hidden />
      </button>
      <div
        role="menu"
        className={`${styles.dropdown} ${isOpen ? styles.dropdownOpen : ''}`}
        aria-hidden={!isOpen}
        // @ts-expect-error inert is not in React types but works in HTML5
        inert={!isOpen ? '' : undefined}
      >
        {loading ? (
          <span className={styles.emptyLabel}>
            <Loader2 size={14} aria-hidden /> Loading…
          </span>
        ) : (
          <>
            {links && links.length === 0 && isAdmin && (
              <span className={styles.emptyLabel}>
                {error ? 'Could not load links' : 'No links configured'}
              </span>
            )}
            {links &&
              links.map((link) => (
                <Link
                  key={link.id}
                  to={link.route}
                  role="menuitem"
                  className={styles.item}
                  onClick={close}
                >
                  <ExternalLink size={14} aria-hidden />
                  <span>{link.label}</span>
                </Link>
              ))}
            {isAdmin && (
              <>
                <div className={styles.divider} />
                <Link
                  to="/settings/quick-links"
                  role="menuitem"
                  className={styles.manageLink}
                  onClick={close}
                >
                  <Settings size={12} aria-hidden />
                  <span>Manage Quick Links</span>
                </Link>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
